import { Question, OASISSchema, OASISFormData, FormValue } from './types';

/**
 * Initializes default values for pseudo questions.
 * Sets pseudo questions with "Yes" option to "Yes" by default.
 *
 * @param formData The current form data
 * @param schema The OASIS schema containing all questions
 * @returns Form data with pseudo question defaults applied
 */
export const initializePseudoQuestionDefaults = (
  formData: OASISFormData,
  schema: OASISSchema
): OASISFormData => {
  const result: OASISFormData = { ...formData };

  const processQuestion = (question: Question) => {
    // If this is a pseudo question with "Yes" option, default to "Yes"
    if (
      question.is_pseudo === true &&
      question.subitems?.['Yes'] &&
      result[question.qid] === undefined
    ) {
      result[question.qid] = 'Yes';
    }

    // Process all subitems recursively
    if (question.subitems) {
      Object.values(question.subitems).forEach(subitems => {
        subitems.forEach(processQuestion);
      });
    }
  };

  Object.values(schema).forEach(questions => {
    questions.forEach(processQuestion);
  });

  return result;
};

/**
 * Checks if a value is meaningful (not empty).
 */
const hasValue = (value: FormValue): boolean => {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }
  return true;
};

/**
 * Checks if any subitem (recursively) has a meaningful value in the form data.
 */
const hasSubitemData = (
  subitems: Question[],
  formData: OASISFormData
): boolean => {
  for (const subitem of subitems) {
    const value = formData[subitem.qid];

    // If this subitem has a meaningful value (and is not a pseudo question), it has data
    if (hasValue(value) && subitem.is_pseudo !== true) {
      return true;
    }

    // Check nested subitems recursively
    if (
      subitem.subitems &&
      typeof value === 'string' &&
      subitem.subitems[value]
    ) {
      if (hasSubitemData(subitem.subitems[value], formData)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Extracts OASIS form data for persistence.
 * Returns data in QID format (the same format used in the UI).
 * Pseudo questions are only included if their subitems have actual data.
 *
 * @param formData The complete form data including pseudo questions
 * @param schema The OASIS schema containing all questions
 * @returns Form data with QIDs as keys
 */
export const getOASISFormData = (
  formData: OASISFormData,
  schema: OASISSchema
): OASISFormData => {
  const result: OASISFormData = {};

  // Process all questions recursively
  const processQuestion = (question: Question) => {
    const value = formData[question.qid];

    // For pseudo questions with "Yes" option, only include if subitems have data
    if (question.is_pseudo === true && question.subitems?.['Yes']) {
      const subitems = question.subitems['Yes'];
      if (hasSubitemData(subitems, formData)) {
        result[question.qid] = 'Yes';
        // Process subitems
        subitems.forEach(subitem => processQuestion(subitem));
      }
      return;
    }

    // Include non-pseudo question value if it has a meaningful value
    if (hasValue(value)) {
      result[question.qid] = value;
    }

    // Process subitems for the currently selected value
    if (
      question.subitems &&
      typeof value === 'string' &&
      question.subitems[value]
    ) {
      question.subitems[value].forEach(subitem => processQuestion(subitem));
    }
  };

  // Process all categories
  Object.values(schema).forEach(questions => {
    questions.forEach(question => processQuestion(question));
  });

  return result;
};

/**
 * Clears subitem data when a parent question's value changes.
 * Returns an object with subitem keys to be cleared (set to undefined).
 *
 * @param question The parent question that changed
 * @param newValue The new value selected for the parent question
 * @param currentFormData Current form data to check for existing subitem values
 * @returns Object with subitem keys that should be cleared
 */
export const getSubitemClearUpdates = (
  question: Question,
  newValue: FormValue,
  currentFormData: OASISFormData
): Partial<OASISFormData> => {
  const clearUpdates: Partial<OASISFormData> = {};

  if (!question.subitems) {
    return clearUpdates;
  }

  // Collect all subitem QIDs recursively
  const collectAllQids = (q: Question, qids: Set<string>) => {
    if (q.subitems) {
      Object.values(q.subitems).forEach(items => {
        items.forEach(item => {
          qids.add(item.qid);
          collectAllQids(item, qids);
        });
      });
    }
  };

  // Get all possible subitem QIDs
  const allQids = new Set<string>();
  collectAllQids(question, allQids);

  // Get QIDs that should remain active (for the new value)
  const activeQids = new Set<string>();
  if (newValue && typeof newValue === 'string' && question.subitems[newValue]) {
    question.subitems[newValue].forEach(subitem => {
      activeQids.add(subitem.qid);
      collectAllQids(subitem, activeQids);
    });
  }

  // Clear inactive subitems that have values
  allQids.forEach(qid => {
    if (!activeQids.has(qid) && currentFormData[qid] !== undefined) {
      clearUpdates[qid] = undefined;
    }
  });

  return clearUpdates;
};
